/**
 * CompanyCardSection — wrapper section moderne pour les pages Detail/Create/Edit.
 *
 * Pattern UX commun établi 9.P.2.R1 — réutilisable R2/R3.
 *
 * Layout : `rounded-xl border-slate-200 bg-white shadow-sm` avec header
 * `border-b` séparé du body. Plus lisible que l'ancien `<Section>` legacy
 * avec `<h2 uppercase tag-line>`.
 *
 * a11y :
 *   - `<section>` sémantique avec `aria-labelledby` pointant le `<h2>` du header
 *   - `id` optionnel pour anchor-links (consommé par CompanySectionNav scroll-spy)
 *   - scroll-margin-top calculé pour ne pas être masqué par le sticky header parent
 */

import { useId, type ReactNode } from "react";

import { cn } from "../../../lib/cn";

type Props = {
  readonly title: string;
  readonly children: ReactNode;
  /** Id pour anchor-link scroll-to / scroll-spy. */
  readonly anchorId?: string;
  /** Action optionnelle à droite du header (ex: lien "Modifier la section"). */
  readonly headerAction?: ReactNode;
  readonly className?: string;
};

export function CompanyCardSection({
  title,
  children,
  anchorId,
  headerAction,
  className,
}: Props) {
  const titleId = useId();
  return (
    <section
      id={anchorId}
      aria-labelledby={titleId}
      className={cn(
        "scroll-mt-32 rounded-xl border border-slate-200 bg-white shadow-sm",
        className,
      )}
    >
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
        <h2 id={titleId} className="text-sm font-semibold text-slate-900">
          {title}
        </h2>
        {headerAction && <div className="shrink-0">{headerAction}</div>}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}
